/**
 * Reversible integer scramble shared by the URL id codecs (run-url-codec,
 * product-url-codec). Keeps global sequential ids — which would leak
 * cross-tenant volume — out of the address bar. Obfuscation, not encryption:
 * the real data guard is the server's tenant ownership filter.
 *
 * id ↦ (id·MULT) mod 2^31, base-36; undone by multiplying by MULT's modular
 * inverse. Non-monotonic, so consecutive ids produce unrelated tokens. Ids
 * never approach 2^31 (~2.1B) in practice. No router imports here on purpose:
 * route modules (and tests that mock the router) import this freely.
 */
const MOD = 1n << 31n;
const MULT = 1103515245n; // odd ⇒ coprime to 2^31 ⇒ a bijection on the ring

function modInverse(a: bigint, m: bigint): bigint {
  let [oldR, r] = [((a % m) + m) % m, m];
  let [oldS, s] = [1n, 0n];
  while (r !== 0n) {
    const q = oldR / r;
    [oldR, r] = [r, oldR - q * r];
    [oldS, s] = [s, oldS - q * s];
  }
  return ((oldS % m) + m) % m;
}

const MULT_INV = modInverse(MULT, MOD);

/** Scramble an id into a base-36 string (no prefix). */
export function scrambleId(id: number): string {
  return ((BigInt(id) * MULT) % MOD).toString(36);
}

/** Undo `scrambleId` for a base-36 string (no prefix). */
export function unscrambleId(base36: string): number {
  return Number((BigInt(parseInt(base36, 36)) * MULT_INV) % MOD);
}
